import { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { HelperText } from '@cbhq/cds-web/controls/HelperText';
import { VStack } from '@cbhq/cds-web/layout';
import { SectionHeader } from '@cbhq/cds-web/section-header/SectionHeader';
import { Text } from '@cbhq/cds-web/typography/Text';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import { InviteEmailSection } from '../components/InviteTray/InviteEmailSection';
import { InviteTray } from '../components/InviteTray/InviteTray';
import { ProductAccessFooter } from '../components/InviteTray/ProductAccessFooter';
import { ProductAccessGroup } from '../components/InviteTray/ProductAccessGroup';
import { ProtectedRoleWarning } from '../components/InviteTray/ProtectedRoleWarning';
import { useInviteEmailField } from '../components/InviteTray/useInviteEmailField';
import { useProtectedRoleWarning } from '../components/InviteTray/useProtectedRoleWarning';
import { StepUpTwoFactorModal } from '../components/StepUpTwoFactorModal';
import { UnsavedChangesAlert } from '../components/UnsavedChangesAlert';
import { useInviteSend } from '../hooks/useInviteSend';
import { useLocalizedProducts } from '../hooks/useLocalizedProducts';
import { useProductSelection } from '../hooks/useProductSelection';
import { useRoles } from '../hooks/useRoles';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { useWorkspaceTeam } from '../hooks/useWorkspaceTeam';
import { hasGatedRole } from '../utils/stepUp';

const messages = defineMessages({
  accessHeading: {
    id: 'Team.inviteTray.accessHeading',
    defaultMessage: 'Access',
    description: 'Heading for the product access section of the invite tray',
  },
  inviteSuccess: {
    id: 'Team.singleLEInviteTray.inviteSuccess',
    defaultMessage: 'Invite sent',
    description: 'Toast shown after a single legal entity invite is successfully sent',
  },
  inviteError: {
    id: 'Team.singleLEInviteTray.inviteError',
    defaultMessage: 'Error sending invite',
    description: 'Toast shown when sending a single legal entity invite fails',
  },
  productAccessRequiredError: {
    id: 'Team.inviteTray.productAccessRequiredError',
    defaultMessage: 'You must select at least one product',
    description: 'Inline error shown when no product access has been selected on submit',
  },
});

type SingleLEInviteTrayParams = {
  onClose: () => void;
};

export function SingleLEInviteTray({ onClose }: SingleLEInviteTrayParams) {
  const { formatMessage } = useIntl();
  const { selectedOrganization } = useOrganization();
  const userProfile = useUserProfile();
  const currentUserEmail = userProfile?.email;
  const organizationId = selectedOrganization?.organizationId ?? '';

  const { members: workspaceMembers, pendingInvites } = useWorkspaceTeam(organizationId);

  const {
    email,
    emailError,
    setEmailError,
    handleEmailChange,
    validate: validateEmail,
  } = useInviteEmailField({
    currentUserEmail,
    members: workspaceMembers,
    pendingInvites,
  });
  const [accessError, setAccessError] = useState<string | null>(null);

  const localizedProducts = useLocalizedProducts();

  const legalEntityIds = useMemo(
    () =>
      (selectedOrganization?.legalEntities ?? [])
        .map((le) => le.legalEntityId)
        .filter((id): id is string => Boolean(id)),
    [selectedOrganization?.legalEntities],
  );

  const { rolesByProductPerEntity } = useRoles();
  const leRolesByProduct = useMemo(
    () => rolesByProductPerEntity[legalEntityIds[0] ?? ''] ?? {},
    [rolesByProductPerEntity, legalEntityIds],
  );

  const filteredLocalizedProducts = useMemo(
    () =>
      localizedProducts.filter(({ productKey }) => (leRolesByProduct[productKey]?.length ?? 0) > 0),
    [localizedProducts, leRolesByProduct],
  );

  const {
    accessRows,
    selectedRoles,
    selectedCount,
    totalCount,
    allProductsSelected,
    handleSelectAll,
    handleDeselectAll,
  } = useProductSelection({
    products: filteredLocalizedProducts,
    rolesByProduct: leRolesByProduct,
  });

  useEffect(() => {
    if (selectedRoles.length > 0) {
      setAccessError(null);
    }
  }, [selectedRoles.length]);

  const hasUnsavedInvite = useMemo(
    () => email.length > 0 || selectedRoles.length > 0,
    [email.length, selectedRoles.length],
  );

  const isGatedMutation = useMemo(() => hasGatedRole(selectedRoles), [selectedRoles]);
  const protectedRoleSelections = useMemo(
    () =>
      legalEntityIds[0] == null ? [] : [{ legalEntityId: legalEntityIds[0], roles: selectedRoles }],
    [legalEntityIds, selectedRoles],
  );
  const protectedRoleWarning = useProtectedRoleWarning(protectedRoleSelections);

  const { isSubmitting, sendInvite, stepUpModalProps } = useInviteSend({
    organizationId,
    email,
    selectedRoles,
    legalEntityIds,
    isGatedRoleMutation: isGatedMutation,
    messages,
    setEmailError,
    onClose,
  });

  const handleInvite = useCallback(() => {
    const nextEmailError = validateEmail();
    const nextAccessError =
      selectedRoles.length === 0 || legalEntityIds.length === 0
        ? formatMessage(messages.productAccessRequiredError)
        : null;

    setEmailError(nextEmailError);
    setAccessError(nextAccessError);

    if (nextEmailError != null || nextAccessError != null) {
      return;
    }

    sendInvite();
  }, [
    formatMessage,
    legalEntityIds.length,
    selectedRoles.length,
    setEmailError,
    sendInvite,
    validateEmail,
  ]);

  const { trayRef, isAlertVisible, handleBeforeClose, handleKeepEditing, handleDiscard } =
    useUnsavedChangesGuard(hasUnsavedInvite);

  return (
    <>
      <InviteTray
        trayRef={trayRef}
        onClose={onClose}
        onInvite={handleInvite}
        beforeClose={handleBeforeClose}
        isSubmitting={isSubmitting}
      >
        <InviteEmailSection
          email={email}
          onEmailChange={handleEmailChange}
          errorMessage={emailError}
        />

        <VStack width="100%" gap={2}>
          <SectionHeader
            title={<Text font="headline">{formatMessage(messages.accessHeading)}</Text>}
            paddingX={0}
            paddingY={0}
          />
          <ProductAccessGroup
            rows={accessRows}
            footer={
              <ProductAccessFooter
                selectedCount={selectedCount}
                totalCount={totalCount}
                allSelected={allProductsSelected}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
              />
            }
          />
          {protectedRoleWarning != null && (
            <ProtectedRoleWarning
              roleName={protectedRoleWarning.roleName}
              gracePeriodMs={protectedRoleWarning.gracePeriodMs}
              context="invite"
            />
          )}
          {accessError != null && <HelperText color="fgNegative">{accessError}</HelperText>}
        </VStack>
      </InviteTray>
      <StepUpTwoFactorModal {...stepUpModalProps} />
      <UnsavedChangesAlert
        variant="invite"
        visible={isAlertVisible}
        onKeepEditing={handleKeepEditing}
        onDiscard={handleDiscard}
      />
    </>
  );
}
